import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import { BarGraphIcon1, BarGraphIcon2, BarGraphIcon3 } from "../../../commonUI/Icons/SvgIcon";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const MonthlyTrendChart = ({
  reportData,
  compareData,
  getMetricValue,
  reportYear,
  compareYear,
  selectStyles,
  months,
}) => {
  const [chartType, setChartType] = useState("line");
  const [selectedMetric, setSelectedMetric] = useState("jobsPosted");
  const [isLoading, setIsLoading] = useState(false);

  const chartData = useMemo(() => {
    return months.map((name, index) => {
      const rpt = reportData.find((item) => item.month === index + 1) || {};
      const cmp = compareData.find((item) => item.month === index + 1) || {};

      return {
        month: name.slice(0, 3),
        report: getMetricValue(rpt, selectedMetric),
        compare: getMetricValue(cmp, selectedMetric),
      };
    });
  }, [
    reportData,
    compareData,
    selectedMetric,
    months,
    getMetricValue,
  ]);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 200);
    return () => clearTimeout(timer);
  }, [chartType, selectedMetric]);

  return (
    <div className="monthly-trend-section mt-32">
      <div className="trend-header">
        <div className="trend-left">
          <h3 className="trend-title">Monthly trend</h3>
          <div className="trend-controls">
            <ReactSelect
              value={{
                value: selectedMetric,
                label:
                  selectedMetric === "jobsPosted"
                    ? "Jobs posted"
                    : selectedMetric === "quotes"
                    ? "Quotes"
                    : selectedMetric === "bookings"
                    ? "Bookings"
                    : selectedMetric === "conversionRate"
                    ? "Booking conversion"
                    : selectedMetric === "unquotedJobs"
                    ? "Unquoted jobs"
                    : selectedMetric === "clientRegistrations"
                    ? "Client registrations"
                    : selectedMetric === "driverRegistrations"
                    ? "Driver registrations"
                    : selectedMetric === "refunds"
                    ? "Refunds"
                    : selectedMetric === "reviews"
                    ? "Reviews"
                    : "Unknown",
              }}
              onChange={(opt) => setSelectedMetric(opt.value)}
              options={[
                { value: "jobsPosted", label: "Jobs posted" },
                { value: "quotes", label: "Quotes" },
                { value: "bookings", label: "Bookings" },
                { value: "conversionRate", label: "Booking conversion" },
                { value: "unquotedJobs", label: "Unquoted jobs" },
                { value: "clientRegistrations", label: "Client registrations" },
                { value: "driverRegistrations", label: "Driver registrations" },
                { value: "refunds", label: "Refunds" },
                { value: "reviews", label: "Reviews" },
              ]}
              styles={selectStyles}
              isSearchable={false}
              classNamePrefix="custom-select"
              className="react-select-container"
            />
          </div>
        </div>

        <div className="chart-type-buttons">
          <button
            className={`chart-type-btn ${chartType === "bar" ? "active" : ""}`}
            onClick={() => setChartType("bar")}
            title="Bar Chart"
          >
            <BarGraphIcon1 width="20" height="20" />
          </button>
          <button
            className={`chart-type-btn ${chartType === "area" ? "active" : ""}`}
            onClick={() => setChartType("area")}
            title="Area Chart"
          >
            <BarGraphIcon2 width="20" height="20" />
          </button>
          <button
            className={`chart-type-btn ${chartType === "line" ? "active" : ""}`}
            onClick={() => setChartType("line")}
            title="Line Chart"
          >
            <BarGraphIcon3 width="20" height="20" />
          </button>
        </div>
      </div>

      <div className="chart-wrapper" style={{ position: "relative" }}>
        {isLoading && (
          <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
            borderRadius: "4px",
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              border: "3px solid #e5e7eb",
              borderTop: "3px solid #0066cc",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        <div style={{ display: chartType === "line" ? "block" : "none" }}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: "12px" }} />
              <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="report"
                stroke="#0066cc"
                strokeWidth={2}
                dot={{ fill: "#0066cc", r: 4 }}
                activeDot={{ r: 6 }}
                name={`${reportYear}`}
              />
              <Line
                type="monotone"
                dataKey="compare"
                stroke="#999999"
                strokeWidth={2}
                dot={{ fill: "#999999", r: 4 }}
                activeDot={{ r: 6 }}
                name={`${compareYear}`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: chartType === "bar" ? "block" : "none" }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: "12px" }} />
              <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
              <Legend />
              <Bar
                dataKey="report"
                fill="#0066cc"
                radius={[4, 4, 0, 0]}
                name={`${reportYear}`}
              />
              <Bar
                dataKey="compare"
                fill="#999999"
                radius={[4, 4, 0, 0]}
                name={`${compareYear}`}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ display: chartType === "area" ? "block" : "none" }}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorReport" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0066cc" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#0066cc" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorCompare" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#999999" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#999999" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: "12px" }} />
              <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="report"
                stroke="#0066cc"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorReport)"
                name={`${reportYear}`}
              />
              <Area
                type="monotone"
                dataKey="compare"
                stroke="#999999"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorCompare)"
                name={`${compareYear}`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MonthlyTrendChart;
